import React from "react";
import { HelpCircle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import ActionButtonsSettings from "./ActionButtonsSettings";
import ErrorMessage from "../common/ErrorMessage";
import SuccessMessage from "../common/SuccessMessage";
import LoadingScreen from "../common/LoadingScreen";
import { useLayoutContext } from "../../context/LayoutContext";

const OfferSettings = () => {
  const {
    userSettings,
    setUserSettings,
    systemError,
    setSystemError,
    loading,
    setLoading,
    error,
    setError,
    success,
    setSuccess,
    modalScreen,
    setModalScreen,
    page,
    setPage,
  } = useLayoutContext();
  const { saveProfile } = useAuth();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    try {
      const updatedSettings = userSettings as ProfileAndSettings;
      if (!updatedSettings) {
        console.error("User settings not loaded");
        return;
      }

      console.log("Updated user settings:", updatedSettings);
      // Assuming you have an API endpoint to update the settings
      const returnUpdatedSettings = await saveProfile(updatedSettings);
      setUserSettings(returnUpdatedSettings);
      setSuccess("Configurações atualizadas com sucesso!");
    } catch (error) {
      console.warn("Error updating settings:", error);
      setError("Failed to update settings. Please try again later.");
    } finally {
      setLoading(null);
    }
  };

  if (!userSettings) {
    return <div>Loading...</div>;
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="box">
        <fieldset>
          <legend className="title is-4">Mensagem Personalizada</legend>

          <div className="field is-horizontal">
            <div className="field-label is-normal">
              <label className="label">
                Saudação Inicial
                <span
                  className="has-tooltip-right has-tooltip-arrow"
                  data-tooltip="Mensagem que aparece no início da proposta"
                >
                  <HelpCircle className="ml-1 has-text-info" size={16} />
                </span>
              </label>
            </div>
            <div className="field-body">
              <div className="field">
                <div className="control">
                  <textarea
                    className="textarea"
                    name="offerCustomGreeting"
                    placeholder="Ex: Prezado cliente, agradecemos o seu interesse..."
                    rows={3}
                    defaultValue={
                      userSettings?.offerSettings?.offerCustomGreeting || ""
                    }
                    onChange={(e) => {
                      const updatedSettings = {
                        ...userSettings,
                        offerSettings: {
                          ...userSettings?.offerSettings,
                          offerCustomGreeting: e.target.value,
                        },
                        profile: userSettings?.profile || {}, // Ensure profile is not undefined
                      };
                      setUserSettings(updatedSettings as ProfileAndSettings);
                    }}
                  />
                </div>
                <div className="control mt-2">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      name="offerCustomGreetingEnabled"
                      style={{ marginRight: "8px" }}
                      defaultChecked={
                        userSettings?.offerSettings
                          ?.offerCustomGreetingEnabled || false
                      }
                      onChange={(e) => {
                        const updatedSettings = {
                          ...userSettings,
                          offerSettings: {
                            ...userSettings?.offerSettings,
                            offerCustomGreetingEnabled: e.target.checked,
                          },
                          profile: userSettings?.profile || {}, // Ensure profile is not undefined
                        };
                        setUserSettings(updatedSettings as ProfileAndSettings);
                      }}
                    />
                    Ativar saudação personalizada
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="field is-horizontal">
            <div className="field-label is-normal">
              <label className="label">
                Mensagem de Fecho
                <span
                  className="has-tooltip-right has-tooltip-arrow"
                  data-tooltip="Mensagem que aparece no final da proposta"
                >
                  <HelpCircle className="ml-1 has-text-info" size={16} />
                </span>
              </label>
            </div>
            <div className="field-body">
              <div className="field">
                <div className="control">
                  <textarea
                    className="textarea"
                    name="offerCustomSignature"
                    placeholder="Ex: Disponíveis para esclarecimentos adicionais..."
                    rows={3}
                    defaultValue={
                      userSettings?.offerSettings?.offerCustomSignature || ""
                    }
                    onChange={(e) => {
                      const updatedSettings = {
                        ...userSettings,
                        offerSettings: {
                          ...userSettings?.offerSettings,
                          offerCustomSignature: e.target.value,
                        },
                        profile: userSettings?.profile || {}, // Ensure profile is not undefined
                      };
                      setUserSettings(updatedSettings as ProfileAndSettings);
                    }}
                  />
                </div>
                <div className="control mt-2">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      name="offerCustomSignatureEnabled"
                      style={{ marginRight: "8px" }}
                      defaultChecked={
                        userSettings?.offerSettings
                          ?.offerCustomSignatureEnabled || false
                      }
                      onChange={(e) => {
                        const updatedSettings = {
                          ...userSettings,
                          offerSettings: {
                            ...userSettings?.offerSettings,
                            offerCustomSignatureEnabled: e.target.checked,
                          },
                          profile: userSettings?.profile || {}, // Ensure profile is not undefined
                        };
                        setUserSettings(updatedSettings as ProfileAndSettings);
                      }}
                    />
                    Ativar mensagem de fecho personalizada
                  </label>
                </div>
              </div>
            </div>
          </div>
        </fieldset>
      </div>

      <div className="box">
        <fieldset>
          <legend className="title is-4">Dados da Empresa</legend>

          <div className="field is-horizontal">
            <div className="field-label is-normal">
              <label className="label">Nome da Empresa</label>
            </div>
            <div className="field-body">
              <div className="field">
                <div className="control">
                  <input
                    className="input"
                    type="text"
                    name="offerCompanyName"
                    placeholder="Nome oficial da empresa"
                    defaultValue={
                      userSettings?.offerSettings?.offerCompanyName || ""
                    }
                    onChange={(e) => {
                      const updatedSettings = {
                        ...userSettings,
                        offerSettings: {
                          ...userSettings?.offerSettings,
                          offerCompanyName: e.target.value,
                        },
                        profile: userSettings?.profile || {}, // Ensure profile is not undefined
                      };
                      setUserSettings(updatedSettings as ProfileAndSettings);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="field is-horizontal">
            <div className="field-label is-normal">
              <label className="label">Morada</label>
            </div>
            <div className="field-body">
              <div className="field">
                <div className="control">
                  <textarea
                    className="textarea"
                    name="offerCompanyAddress"
                    placeholder="Rua, nº, andar"
                    rows={3}
                    defaultValue={
                      userSettings?.offerSettings?.offerCompanyAddress || ""
                    }
                    onChange={(e) => {
                      const updatedSettings = {
                        ...userSettings,
                        offerSettings: {
                          ...userSettings?.offerSettings,
                          offerCompanyAddress: e.target.value,
                        },
                        profile: userSettings?.profile || {}, // Ensure profile is not undefined
                      };
                      setUserSettings(updatedSettings as ProfileAndSettings);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="field is-horizontal">
            <div className="field-label is-normal">
              <label className="label">Localidade e Código</label>
            </div>
            <div className="field-body">
              <div className="field is-grouped">
                <div className="control">
                  <input
                    className="input"
                    type="text"
                    name="offerCompanyZipCode"
                    placeholder="Código Postal"
                    defaultValue={
                      userSettings?.offerSettings?.offerCompanyZipCode || ""
                    }
                    onChange={(e) => {
                      const updatedSettings = {
                        ...userSettings,
                        offerSettings: {
                          ...userSettings?.offerSettings,
                          offerCompanyZipCode: e.target.value,
                        },
                        profile: userSettings?.profile || {}, // Ensure profile is not undefined
                      };
                      setUserSettings(updatedSettings as ProfileAndSettings);
                    }}
                  />
                </div>
                <div className="control is-expanded">
                  <input
                    className="input"
                    type="text"
                    name="offerCompanyLocation"
                    placeholder="Localidade"
                    defaultValue={
                      userSettings?.offerSettings?.offerCompanyLocation || ""
                    }
                    onChange={(e) => {
                      const updatedSettings = {
                        ...userSettings,
                        offerSettings: {
                          ...userSettings?.offerSettings,
                          offerCompanyLocation: e.target.value,
                        },
                        profile: userSettings?.profile || {}, // Ensure profile is not undefined
                      };
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="field is-horizontal">
            <div className="field-label is-normal">
              <label className="label">Telefones</label>
            </div>
            <div className="field-body">
              <div className="field is-grouped">
                <div className="control is-expanded">
                  <input
                    className="input"
                    type="text"
                    name="offerCompanyPhone"
                    placeholder="Telefone fixo"
                    defaultValue={
                      userSettings?.offerSettings?.offerCompanyPhone || ""
                    }
                    onChange={(e) => {
                      const updatedSettings = {
                        ...userSettings,
                        offerSettings: {
                          ...userSettings?.offerSettings,
                          offerCompanyPhone: e.target.value,
                        },
                        profile: userSettings?.profile || {}, // Ensure profile is not undefined
                      };
                      setUserSettings(updatedSettings as ProfileAndSettings);
                    }}
                  />
                </div>
                <div className="control is-expanded">
                  <input
                    className="input"
                    type="text"
                    name="offerCompanyMobile"
                    placeholder="Telemóvel"
                    defaultValue={
                      userSettings?.offerSettings?.offerCompanyMobile || ""
                    }
                    onChange={(e) => {
                      const updatedSettings = {
                        ...userSettings,
                        offerSettings: {
                          ...userSettings?.offerSettings,
                          offerCompanyMobile: e.target.value,
                        },
                        profile: userSettings?.profile || {}, // Ensure profile is not undefined
                      };
                      setUserSettings(updatedSettings as ProfileAndSettings);
                    }}
                  />
                </div>
                <div className="control is-expanded">
                  <input
                    className="input"
                    type="text"
                    name="offerCompanyFax"
                    placeholder="Fax"
                    defaultValue={
                      userSettings?.offerSettings?.offerCompanyFax || ""
                    }
                    onChange={(e) => {
                      const updatedSettings = {
                        ...userSettings,
                        offerSettings: {
                          ...userSettings?.offerSettings,
                          offerCompanyFax: e.target.value,
                        },
                        profile: userSettings?.profile || {}, // Ensure profile is not undefined
                      };
                      setUserSettings(updatedSettings as ProfileAndSettings);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="field is-horizontal">
            <div className="field-label is-normal">
              <label className="label">Contactos Online</label>
            </div>
            <div className="field-body">
              <div className="field is-grouped">
                <div className="control is-expanded">
                  <input
                    className="input"
                    type="email"
                    name="offerCompanyEmail"
                    placeholder="Email de contacto"
                    defaultValue={
                      userSettings?.offerSettings?.offerCompanyEmail || ""
                    }
                    onChange={(e) => {
                      const updatedSettings = {
                        ...userSettings,
                        offerSettings: {
                          ...userSettings?.offerSettings,
                          offerCompanyEmail: e.target.value,
                        },
                        profile: userSettings?.profile || {}, // Ensure profile is not undefined
                      };
                      setUserSettings(updatedSettings as ProfileAndSettings);
                    }}
                  />
                </div>
                <div className="control is-expanded">
                  <input
                    className="input"
                    type="text"
                    name="offerCompanyWebsite"
                    placeholder="Website"
                    defaultValue={
                      userSettings?.offerSettings?.offerCompanyWebsite || ""
                    }
                    onChange={(e) => {
                      const updatedSettings = {
                        ...userSettings,
                        offerSettings: {
                          ...userSettings?.offerSettings,
                          offerCompanyWebsite: e.target.value,
                        },
                        profile: userSettings?.profile || {}, // Ensure profile is not undefined
                      };
                      setUserSettings(updatedSettings as ProfileAndSettings);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </fieldset>
      </div>

      <ActionButtonsSettings />
    </form>
  );
};

export default OfferSettings;
